import { useId, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import {
  cn,
  useBodyScrollLock,
  useEscapeKey,
  useFocusTrap,
  useInertOutside,
  usePrefersReducedMotion,
} from '@/lib/utils';
import { Portal } from './Portal';
import { IconButton } from './IconButton';
import './Dialog.css';

export type DialogSize = 'sm' | 'md' | 'lg';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  /** Footer actions, right-aligned (e.g. cancel + confirm buttons). */
  footer?: ReactNode;
  size?: DialogSize;
  children?: ReactNode;
  /** Disable Escape / backdrop close while a destructive action is in flight. */
  dismissible?: boolean;
}

/**
 * Focused modal for confirm-destructive or focused-create flows only — prefer
 * inline/progressive UI otherwise. Traps focus, makes the app behind it `inert`,
 * locks scroll, closes on Escape and backdrop click, and restores focus on close.
 * Title/description are wired to aria-labelledby/-describedby.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  footer,
  size = 'md',
  children,
  dismissible = true,
}: DialogProps): JSX.Element | null {
  // Estado, e não ref: o Portal só põe o painel no DOM no commit seguinte ao da
  // abertura, e é a chegada dele que precisa disparar a trava de foco e o inert.
  const [panel, setPanel] = useState<HTMLDivElement | null>(null);
  const titleId = useId();
  const descId = useId();
  const reduceMotion = usePrefersReducedMotion();

  useEscapeKey(() => dismissible && onClose(), open);
  useInertOutside(open, panel);
  useFocusTrap(panel, open);
  useBodyScrollLock(open);

  if (!open) return null;

  return (
    <Portal>
      <div className={cn('dialog-overlay', reduceMotion && 'dialog-overlay--instant')}>
        <button
          type="button"
          className="dialog-backdrop"
          aria-label="Fechar"
          tabIndex={-1}
          onClick={() => dismissible && onClose()}
        />
        <div
          ref={setPanel}
          className={cn('dialog', `dialog--${size}`)}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={description ? descId : undefined}
        >
          <div className="dialog__header">
            <div className="dialog__heading">
              <h2 id={titleId} className="dialog__title">
                {title}
              </h2>
              {description && (
                <p id={descId} className="dialog__description">
                  {description}
                </p>
              )}
            </div>
            <IconButton label="Fechar" icon={<X />} size="sm" onClick={onClose} />
          </div>
          {children && <div className="dialog__body">{children}</div>}
          {footer && <div className="dialog__footer">{footer}</div>}
        </div>
      </div>
    </Portal>
  );
}
